import React, { useRef } from 'react';
import { formatDate } from '../../utils/dateFormatter';
import { printDocument } from '../ui/PrintLayout';

export const InvoicePrintView = ({ invoice }) => {
  const invoiceTypeLabel = invoice.invoice_type === 'credit_note'
    ? 'Credit Note'
    : invoice.invoice_type === 'debit_note'
    ? 'Debit Note'
    : 'Invoice';

  return (
    <div className="print-container">
      {/* Header */}
      <div className="print-header text-center mb-6 pb-4 border-b-2 border-gray-800">
        <h1 className="text-2xl font-bold mb-1">CLADAKIS FOR READY MADE GARMENTS</h1>
        <p className="text-sm text-gray-600">Garment Manufacturing & Trading</p>
      </div>

      {/* Document Title */}
      <div className="text-center mb-6">
        <h2 className="text-xl font-semibold uppercase">{invoiceTypeLabel}</h2>
        <p className="text-lg mt-1">#{invoice.invoice_number}</p>
      </div>

      {/* Invoice Info */}
      <div className="info-grid grid grid-cols-2 gap-4 mb-6">
        <div>
          <div className="info-item mb-2">
            <span className="info-label font-semibold">Client:</span>
            <p>{invoice.client_name}</p>
          </div>
          <div className="info-item">
            <span className="info-label font-semibold">Order Reference:</span>
            <p>#{invoice.order_id}</p>
          </div>
        </div>
        <div className="text-right">
          <div className="info-item mb-2">
            <span className="info-label font-semibold">Date:</span>
            <p>{formatDate(invoice.date)}</p>
          </div>
          <div className="info-item">
            <span className="info-label font-semibold">Invoice No:</span>
            <p>{invoice.invoice_number}</p>
          </div>
        </div>
      </div>

      {/* Items Table */}
      <table className="print-table w-full border-collapse mb-6">
        <thead>
          <tr>
            <th className="border border-gray-800 p-2 bg-gray-100 text-left">#</th>
            <th className="border border-gray-800 p-2 bg-gray-100 text-left">Item Code</th>
            <th className="border border-gray-800 p-2 bg-gray-100 text-left">Description</th>
            <th className="border border-gray-800 p-2 bg-gray-100 text-center">Qty</th>
            <th className="border border-gray-800 p-2 bg-gray-100 text-right">Unit Price</th>
            <th className="border border-gray-800 p-2 bg-gray-100 text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          {invoice.items && invoice.items.map((item, idx) => {
            const unitPrice = item.unit_price ?? 0;
            return (
              <tr key={idx}>
                <td className="border border-gray-800 p-2">{idx + 1}</td>
                <td className="border border-gray-800 p-2">{item.item_code || '—'}</td>
                <td className="border border-gray-800 p-2">{item.description}</td>
                <td className="border border-gray-800 p-2 text-center">{item.quantity}</td>
                <td className="border border-gray-800 p-2 text-right">{unitPrice.toFixed(2)}</td>
                <td className="border border-gray-800 p-2 text-right">{(item.quantity * unitPrice).toFixed(2)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Totals */}
      <div className="totals text-right">
        <div className="totals-row flex justify-end gap-8 mb-1">
          <span className="totals-label">Subtotal:</span>
          <span className="totals-value font-mono w-32">{invoice.subtotal.toFixed(2)} EGP</span>
        </div>
        {invoice.discount > 0 && (
          <div className="totals-row flex justify-end gap-8 mb-1">
            <span className="totals-label">Discount:</span>
            <span className="totals-value font-mono w-32">-{invoice.discount.toFixed(2)} EGP</span>
          </div>
        )}
        {invoice.vat_percentage > 0 && (
          <div className="totals-row flex justify-end gap-8 mb-1">
            <span className="totals-label">VAT ({invoice.vat_percentage}%):</span>
            <span className="totals-value font-mono w-32">{invoice.vat_amount.toFixed(2)} EGP</span>
          </div>
        )}
        <div className="grand-total totals-row flex justify-end gap-8 pt-2 mt-2 border-t-2 border-gray-800">
          <span className="totals-label text-lg font-bold">Total:</span>
          <span className="totals-value text-lg font-bold font-mono w-32">{invoice.total.toFixed(2)} EGP</span>
        </div>
      </div>

      {/* Signature Section */}
      <div className="signature-section flex justify-between mt-16">
        <div className="signature-line w-48 border-t border-gray-800 pt-1 text-center">
          <p className="text-sm">Authorized Signature</p>
        </div>
        <div className="signature-line w-48 border-t border-gray-800 pt-1 text-center">
          <p className="text-sm">Client Signature</p>
        </div>
      </div>

      {/* Footer */}
      <div className="print-footer mt-12 pt-4 border-t border-gray-300 text-center text-sm text-gray-500">
        <p>Thank you for your business!</p>
        <p className="mt-1">Printed on: {formatDate(new Date())}</p>
      </div>
    </div>
  );
};

export const useInvoicePrint = () => {
  const printRef = useRef(null);

  const handlePrint = (invoice) => {
    const printWindow = window.open('', '_blank');
    const invoiceTypeLabel = invoice.invoice_type === 'credit_note'
      ? 'Credit Note'
      : invoice.invoice_type === 'debit_note'
      ? 'Debit Note'
      : 'Invoice';

    const grouped = {};
    (invoice.items || []).forEach(item => {
      const key = item.item_code || item.description;
      if (!grouped[key]) {
        grouped[key] = { item_code: item.item_code || '—', quantity: 0, revenue: 0, unit_price: item.unit_price ?? 0 };
      }
      grouped[key].quantity += (item.quantity || 0);
      grouped[key].revenue += (item.quantity || 0) * (item.unit_price ?? 0);
    });
    const itemsHtml = Object.entries(grouped).map(([key, g], idx) => {
      const unitPrice = g.quantity > 0 ? g.revenue / g.quantity : g.unit_price;
      return `
      <tr>
        <td style="border: 1px solid #333; padding: 8px;">${idx + 1}</td>
        <td style="border: 1px solid #333; padding: 8px;">${g.item_code}</td>
        <td style="border: 1px solid #333; padding: 8px;">${key}</td>
        <td style="border: 1px solid #333; padding: 8px; text-align: center;">${g.quantity}</td>
        <td style="border: 1px solid #333; padding: 8px; text-align: right;">${unitPrice.toFixed(2)}</td>
        <td style="border: 1px solid #333; padding: 8px; text-align: right;">${g.revenue.toFixed(2)}</td>
      </tr>
    `;}).join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${invoiceTypeLabel} #${invoice.invoice_number}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; padding: 30px; color: #000; background: #fff; }
            .container { max-width: 800px; margin: 0 auto; }
            .header { text-align: center; margin-bottom: 30px; padding-bottom: 15px; border-bottom: 2px solid #333; }
            .header h1 { font-size: 22pt; margin-bottom: 5px; }
            .header p { font-size: 10pt; color: #666; }
            .doc-title { text-align: center; margin-bottom: 25px; }
            .doc-title h2 { font-size: 16pt; text-transform: uppercase; }
            .doc-title p { font-size: 14pt; margin-top: 5px; }
            .info-grid { display: flex; justify-content: space-between; margin-bottom: 25px; }
            .info-left, .info-right { width: 48%; }
            .info-right { text-align: right; }
            .info-item { margin-bottom: 8px; }
            .info-label { font-weight: bold; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 25px; }
            th { background-color: #f0f0f0; border: 1px solid #333; padding: 10px; text-align: left; font-weight: bold; }
            td { border: 1px solid #333; padding: 8px; }
            .totals { text-align: right; margin-bottom: 30px; }
            .totals-row { display: flex; justify-content: flex-end; margin-bottom: 5px; }
            .totals-label { width: 150px; }
            .totals-value { width: 120px; text-align: right; font-family: monospace; }
            .grand-total { border-top: 2px solid #333; padding-top: 10px; margin-top: 10px; font-size: 14pt; font-weight: bold; }
            .signatures { display: flex; justify-content: space-between; margin-top: 60px; }
            .sig-line { width: 200px; border-top: 1px solid #333; padding-top: 5px; text-align: center; font-size: 10pt; }
            .footer { margin-top: 50px; padding-top: 15px; border-top: 1px solid #ccc; text-align: center; font-size: 9pt; color: #666; }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h1>CLADAKIS FOR READY MADE GARMENTS</h1>
              <p>Garment Manufacturing & Trading</p>
            </div>
            <div class="doc-title">
              <h2>${invoiceTypeLabel}</h2>
              <p>#${invoice.invoice_number}</p>
            </div>
            <div class="info-grid">
              <div class="info-left">
                <div class="info-item"><span class="info-label">Client:</span> ${invoice.client_name}</div>
                <div class="info-item"><span class="info-label">Order Reference:</span> #${invoice.order_id}</div>
              </div>
              <div class="info-right">
                <div class="info-item"><span class="info-label">Date:</span> ${formatDate(invoice.date)}</div>
                <div class="info-item"><span class="info-label">Invoice No:</span> ${invoice.invoice_number}</div>
              </div>
            </div>
            <table>
              <thead>
                <tr>
                  <th style="width: 40px;">#</th>
                  <th style="width: 100px;">Item Code</th>
                  <th>Description</th>
                  <th style="width: 80px; text-align: center;">Qty</th>
                  <th style="width: 110px; text-align: right;">Unit Price</th>
                  <th style="width: 120px; text-align: right;">Total</th>
                </tr>
              </thead>
              <tbody>
                ${itemsHtml}
              </tbody>
            </table>
            <div class="totals">
              <div class="totals-row">
                <span class="totals-label">Subtotal:</span>
                <span class="totals-value">${invoice.subtotal.toFixed(2)} EGP</span>
              </div>
              ${invoice.discount > 0 ? `
              <div class="totals-row">
                <span class="totals-label">Discount:</span>
                <span class="totals-value">-${invoice.discount.toFixed(2)} EGP</span>
              </div>` : ''}
              ${invoice.vat_percentage > 0 ? `
              <div class="totals-row">
                <span class="totals-label">VAT (${invoice.vat_percentage}%):</span>
                <span class="totals-value">${invoice.vat_amount.toFixed(2)} EGP</span>
              </div>` : ''}
              <div class="totals-row grand-total">
                <span class="totals-label">Total:</span>
                <span class="totals-value">${invoice.total.toFixed(2)} EGP</span>
              </div>
            </div>
            <div class="signatures">
              <div class="sig-line">Authorized Signature</div>
              <div class="sig-line">Client Signature</div>
            </div>
            <div class="footer">
              <p>Thank you for your business!</p>
              <p>Printed on: ${formatDate(new Date())}</p>
            </div>
          </div>
          <script>
            window.onload = function() { window.print(); window.onafterprint = function() { window.close(); } }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return { printRef, handlePrint };
};
